import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  FormGroup,
  FormLabel,
  FormControl,
  Alert,
} from "react-bootstrap";
import api from "../../../../utils/ApiMethod";

interface AddCrcModalProps {
  onAdded?: () => void;
}

const AddCrcModal = ({ onAdded }: AddCrcModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const [crcName, setCrcName] = useState("");
  const [blockName, setBlockName] = useState("");
  const [blocks, setBlocks] = useState<any[]>([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [adding, setAdding] = useState(false);
  const [fieldRequired, setFieldRequired] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleShow = () => setShowModal(true);
  const handleClose = () => {
    setShowModal(false);
    setCrcName("");
    setBlockName("");
    setFieldRequired("");
    setErrorMessage("");
  };

  // Most visitors to the CRC page never open this modal, so the block list
  // (used only for this dropdown) is fetched lazily on first open rather
  // than eagerly when the page loads — then cached in state for any
  // subsequent open during this visit.
  useEffect(() => {
    if (!showModal || blocks.length > 0) return;
    const fetchBlocks = async () => {
      setLoadingBlocks(true);
      try {
        const response = await api.fetchData("/api/block/list");
        setBlocks(response?.data?.blocks ?? []);
      } catch (error) {
        console.error("Error fetching block list:", error);
      } finally {
        setLoadingBlocks(false);
      }
    };
    fetchBlocks();
  }, [showModal, blocks.length]);

  const handleAddNewCRC = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!crcName || !blockName) {
      setFieldRequired("All fields are required.");
      return;
    }
    try {
      setAdding(true);
      const formData = new FormData();
      formData.append("CRCNameWithCode", crcName);
      formData.append("BlockNameWithCode", blockName);
      const response = await api.createData("/add/new/crc", formData);
      if (response.status === 200) {
        setResponseMessage(
          response?.data?.message || "New CRC added successfully."
        );
        onAdded && onAdded();
      } else {
        setErrorMessage(
          response?.message || "Something went wrong while adding new CRC."
        );
      }
    } catch (error) {
      console.error("Error while adding CRC:", error);
      setErrorMessage("Something went wrong while adding new CRC.");
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    if (fieldRequired) {
      const timer = setTimeout(() => setFieldRequired(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [fieldRequired]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (responseMessage) {
      const timer = setTimeout(() => {
        setResponseMessage("");
        handleClose();
      }, 1200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseMessage]);

  return (
    <>
      <Button
        variant="primary"
        onClick={handleShow}
        className="d-flex align-items-center gap-1"
      >
        <i className="uil uil-plus"></i> Add CRC
      </Button>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New CRC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fieldRequired && (
            <Alert variant="warning" className="py-2 mb-3">
              {fieldRequired}
            </Alert>
          )}
          {responseMessage && (
            <Alert variant="success" className="py-2 mb-3">
              {responseMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" className="py-2 mb-3">
              {errorMessage}
            </Alert>
          )}
          <Form onSubmit={handleAddNewCRC}>
            <FormGroup>
              <FormLabel>
                CRC Name with Code <span className="text-danger">*</span>
              </FormLabel>
              <FormControl
                type="text"
                placeholder="e.g. GANDHI MEMORIAL +2 HIGH SCHOOL-01(10240507109)"
                value={crcName}
                onChange={(e) => setCrcName(e.target.value)}
              />
            </FormGroup>
            <FormGroup className="mt-3">
              <FormLabel>
                Select Block <span className="text-danger">*</span>
              </FormLabel>
              <FormControl
                as="select"
                value={blockName}
                onChange={(e) => setBlockName(e.target.value)}
              >
                <option value="">
                  {loadingBlocks ? "Loading blocks..." : "Select Block"}
                </option>
                {blocks.map((item: any) => (
                  <option value={item?.region_name} key={item?._id}>
                    {item?.region_name}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddNewCRC} disabled={adding}>
            {adding ? "Adding..." : "Add CRC"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddCrcModal;
